<template>
  <div class="tab-user-list">

    <ui-table-wrapper
      ref="table"
      :provider="provider"
      :fields="fields"
      :size="UI.SIZE.MEDIUM"
      primary-key="evenement_id"
      striped
      hover
    >
      <template
        v-if="providerCallCount === 0"
        #table-busy
      >
        <layout-placeholder-table-body
          :count="context.perPage"
          striped
        />
      </template>
      <template #empty>
        <ui-well class="text-align-center">
          Aucun résultat
        </ui-well>
      </template>

      <template #cell(actions)="data">
        <div class="tab-user-list__actions__inner">

          <ui-buffered-pending-icon
            :is-pending="isRowPending(data.item.evenement_id)"
            :type="UI.SPINNER_TYPE.BORDER"
          >
            <template #default="{ onPreBufferPending, onBufferPending }">
              <div :id="`parameters-pending-list-dropdown-${data.item.evenement_id}`">
                <ui-dropdown-wrapper
                  :menu-variant="UI.VARIANT.PRIMARY"
                  :status="onPreBufferPending ?
                    UI.STATUS.IDLED :
                    (
                      onBufferPending ?
                        UI.STATUS.DISABLED :
                        UI.STATUS.ENABLED
                    )
                  "
                  placement="left-start"
                >
                  <template #button-content>
                    <ui-button-icon
                      name="more_vert"
                      :variant="UI.VARIANT.DARK"
                      :size="UI.SIZE.SMALL"
                      square
                      ripple
                    />
                  </template>
                  <template #default>
                    <b-dropdown-item
                      :disabled="
                        !hasACL(ACLS.PERMISSION_MANAGEMENT_CREATE)
                      "
                      @click="() => resendCallback(data.item.evenement_id)"
                    >
                      <ui-icon
                        name="forward_to_inbox"
                      />
                      Renvoyer l'invitation
                    </b-dropdown-item>
                    <b-dropdown-item
                      :disabled="
                        !hasACL(ACLS.PERMISSION_MANAGEMENT_DELETE)
                      "
                      @click="() => deletionIntent(data.item.evenement_id)"
                    >
                      <ui-icon
                        name="delete"
                      />
                      Supprimer l'invitation
                    </b-dropdown-item>
                  </template>
                </ui-dropdown-wrapper>
              </div>
            </template>
          </ui-buffered-pending-icon>

          <ui-popover-wrapper
            :ref="`popover-${data.item.evenement_id}`"
            :target="`parameters-pending-list-dropdown-${data.item.evenement_id}`"
            placement="left"
            size="fluid"
            no-animation
            manual
          >
            <template #popover>
              <ui-wad class="d-flex gap-3 align-items-center">
                <ui-wad class="font-weight-light">
                  Confirmer&nbsp;?
                </ui-wad>
                <ui-button-icon
                  name="check"
                  square
                  :elevation="UI.ELEVATION.LEVEL_2"
                  :variant="UI.VARIANT.DARK"
                  @click="() => deletionConfirmation(data.item.evenement_id)"
                />
              </ui-wad>
            </template>
          </ui-popover-wrapper>

        </div>
      </template>

      <template #cell(user)="data">
        {{ data.item.fullname }}<span
          class="color-gray-dark">&nbsp;&mdash;&nbsp;{{ data.item.email }}</span>
        <ui-badge-common
          v-if="data.item.is_expired"
          class="ml-1 font-size-13px"
          variant="danger"
        >Expirée</ui-badge-common>
      </template>

      <template #cell(companyCounter)="data">
        <ui-wad v-if="data.item.all_societes">
          <span class="font-size-90 line-height-0 d-inline-block font-weight-normal text-transform-uppercase ">Toutes</span>
        </ui-wad>

        <ui-wad v-if="!data.item.all_societes">
          <span class="font-size-100 line-height-0 font-family-monospace d-inline-block font-weight-normal">{{
            data.item.nb_societe
          }}</span>
          <ui-blank class="mr-0.5" />
          <span class="font-size-80 font-weight-normal text-transform-uppercase color-gray-dark">
            {{
              data.item.nb_societe > 1 ?
                `societés` :
                `societé`
            }}
          </span>
        </ui-wad>
      </template>

    </ui-table-wrapper>


    <div class="tab-user-list__footer">

      <ui-blank />

      <ui-wad class="d-flex align-items-center justify-content-center">
        <ui-table-pagination
          v-model="context.currentPage"
          :total-rows="context.total"
          :per-page="context.perPage"
        />
      </ui-wad>

      <ui-wad class="d-flex align-items-center gap-1 justify-content-flex-end">
        <ui-form-select
        v-model="context.perPage"
        :options="perPageOptions"
        :size="UI.SIZE.SMALL"
        class="w-auto"
        />
        <ui-wad class="font-size-90 font-weight-light">
          résultats par page
        </ui-wad>
      </ui-wad>

    </div>

  </div>
</template>

<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/variables
@import ~/assets/sass/modules/table/members

.tab-user-list
  $self: &

  padding: $layout-spacer-y $layout-spacer-x

  &__footer
    margin-top: $layout-spacer-y
    display: grid
    grid-template-columns: repeat(3, 1fr)
    align-items: center

  /////////////////////////////////

  $actions-column-width: 44px
  $date-column-width: 150px
  $count-column-width: 240px

  :deep(.b-table.table)
    @include table-presets

    /// Deletion UI
    ///   - kept row strategy
    ///   - opacity with slighter darkened background, echoing disabled state
    ///   - removing events via CSS
    #{ $self }__deleted-row
      opacity: 0.1
      pointer-events: none
      transition: $transition-base
      background-color: $gray-95

    /// Columns
    #{ $self }__actions
      width: $actions-column-width

    #{ $self }__count
      width: $date-column-width

    #{ $self }__date
      width: $date-column-width

    #{ $self }__identity
      padding-left: space-x(2)

    /// Cells
    tbody

      #{ $self }__actions
        @include table-cell-presets

      #{ $self }__date
        word-spacing: -1.5px
        color: $gray-deep

</style>

<script lang="ts">
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { useDataOnlyNavigationContext } from '~/composables/layout/navigation'
import { type UserInvitation } from '~/stores/client/admin/pending'
import { useTableProvider, type Context } from '~/composables/layout/table-provider'
import { TAB_MENU, useStore_client_admin_list, type ListFilters } from '~/stores/client/admin/list'
import type { UiPopoverWrapper, UiTableWrapper } from '#components'
import { wrap as wrapInput } from '~/utils/infra/common/types'
import Console from '~/utils/lib/core/console'
import { append, clone, equals, includes, Maybe, pipe_, reject, replace, sustainedPromise, tapCatch, toString } from '~/utils/lib/core/prelude'
import { parseDatetime } from '~/utils/lib/core/datetime'
import type { ComponentExposed } from 'vue-component-type-helpers'
import { useToastBuilder } from '~/composables/layout/toast'
import { useStore_client_admin_pending } from '~/stores/client/admin/pending'
import { useAccessControl } from '~/composables/layout/access-control'
import { querySelector } from '~/utils/lib/core/dom'
import { useLegacyTemplateRef } from '~/composables/core/dom'

const console = new Console('component:pending-list')

export default defineNuxtComponent({
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.DATA_ONLY,
      menuItem: MENU_ITEM.PARAMETERS_USER_MANAGEMENT,
    })
    const navigationContext = useDataOnlyNavigationContext()

    const ref_table
      = useTemplateRef<ComponentExposed<typeof UiTableWrapper>>('table')

    const defaultProviderValue = {
      context: {
        perPage: 50
      }
    }

    const tableProvider = useTableProvider<UserInvitation, ListFilters>(
      ref_table,
      defaultProviderValue
    )

    const ToastBuilder = useToastBuilder()

    const refSelector = useLegacyTemplateRef()

    const store_admin_list = useStore_client_admin_list()
    const store_pending_list = useStore_client_admin_pending()

    store_admin_list.setTabItem(TAB_MENU.PENDING_INVITATION_LIST)

    return {
      ...useAccessControl(),
      ToastBuilder,
      ...navigationContext,
      ...tableProvider,
      refSelector,
      store_admin_list,
      store_pending_list,
    }
  },
  data() {
    return {
      Maybe,
      TAB_MENU,
      fields: [
        {
          key: 'user',
          label: `Utilisateurs`,
          class: 'tab-user-list__identity'
        },
        {
          key: 'companyCounter',
          label: `Sociétés`,
          class: 'tab-user-list__count'
        },
        {
          key: 'invitation_date',
          label: `Date d'invitation`,
          class: 'tab-user-list__date',
          formatter: ({ value }) => pipe_(
            value,
            parseDatetime(
              {
                inputFormat: undefined,
                outputFormat: 'DD/MM/YY',
              }
            ),
            replace(/\//g, ' / ') // (for "word-spacing" CSS rule)
          ),
        },
        {
          key: 'actions',
          label: '',
          class: 'tab-user-list__actions'
        },
      ],
      perPageOptions: [
        {
          value: 50,
          text: '50'
        },
        {
          value: 100,
          text: '100',
        },
        {
          value: 200,
          text: '200'
        }
      ],
      pendingList: [] as Array<UserInvitation["evenement_id"]>,
    }
  },
  created() {
    /**
     * @desc Providing `store_admin_list.filters` to input request
     */
    this.consumer = (context :Context<ListFilters>) => {
      const options = {
        input: wrapInput(context),
        uuidData: this.fromStructureLevel().structure.uuidData
      }

      options.input.filters = clone(this.store_admin_list.filters)

      return this.store_pending_list.list(options)
        .catch(tapCatch(err => {
          console.warn('consumer', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Liste des invitations`)
            .show()
        }))
    }
    /**
     * @desc Deep listening of the `store_admin_list.filters` to trigger
     *       new list update
     */
    this.$watch(
      instance => instance.store_admin_list.filters,
      () => this.refreshTable(),
      {
        immediate: false,
        deep: true,
      }
    )
  },
  methods: {
    isRowPending(evenement_id) {
      return includes(evenement_id, this.pendingList)
    },
    resendCallback(evenement_id) {
      const { uuidData } = this.fromStructureLevel().structure

      this.pendingList = append(evenement_id, this.pendingList)

      return sustainedPromise(
          400,
          this.store_pending_list.resend({ uuidData, evenement_id })
        )
        .then(() => this.refreshTable())
        .then(() => {
          this.ToastBuilder
            .success()
            .title(`Invitation renvoyée !`)
            .show()
        })
        .catch(err => {
          console.warn('resendCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Renvoie de l'invitation`)
            .show()
        })
        .finally(() => {
          this.pendingList = reject(
            equals(evenement_id),
            this.pendingList
          )
        })
    },
    deletionIntent(evenement_id) {
      this.refSelector<InstanceType<typeof UiPopoverWrapper>>(`popover-${evenement_id}`)
        .map(instance => instance.show())
    },
    deletionConfirmation(evenement_id) {
      const { uuidData } = this.fromStructureLevel().structure

      this.pendingList = append(evenement_id, this.pendingList)

      this.refSelector<InstanceType<typeof UiPopoverWrapper>>(`popover-${evenement_id}`)
        .map(instance => instance.hide())

      return sustainedPromise(
          400,
          this.store_pending_list.delete({ uuidData, evenement_id })
        )
        .then(() => removeRow(evenement_id))
        .then(() => this.refreshTable())
        .catch(err => {
          console.warn('deletionConfirmation', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Suppression d'une invitation`)
            .show()
        })
        .finally(()  => {
          this.pendingList = reject(
            equals(evenement_id),
            this.pendingList
          )
        })
    },
  },
})
/**
 * @function removeRow
 * @param {string} evenement_id
 * @return {void}
 */
function removeRow(evenement_id) {
  const className = 'tab-user-list__deleted-row'

  // start delete animation
  querySelector(`.tab-user-list tr[data-pk="${evenement_id}"]`)
   .map( element => element.classList.add(className) )
}
</script>
